import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../lib/api';
import { slotKey } from '../lib/slotSelection';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '@/lib/utils';

/*
 * Pure helpers, kept tiny and outside the component body so they are trivial to unit test without
 * mounting anything. All of them read only the two objects the server hands back - a binding and
 * (where relevant) the profile it points at.
 */

/** The server reports STOP+RUNNING while the worker winds a session down - never invent "Stopped"
 *  before it actually reports that. */
export function isStopping(binding) {
  return !!binding && binding.desired === 'STOP' && binding.state === 'RUNNING';
}

/** One word for the badge, honest about the in-between and error states rather than a green lie. */
export function stateLabel(binding) {
  if (!binding) return 'Not connected';
  if (isStopping(binding)) return 'Stopping';
  switch (binding.state) {
    case 'RUNNING': return 'Running';
    case 'STOPPED': return 'Stopped';
    case 'LOGGED_OUT': return 'Logged out';
    case 'REVIEW': return 'Needs review';
    case 'NEEDS_REVIEW': return 'Needs review';
    case 'QUEUED': return 'Waiting for PC';
    case 'WAITING': return 'Waiting for next session';
    case 'STALE': return 'Stale';
    default: return binding.state || 'Unknown';
  }
}

function findProfile(profiles, installId, profileId) {
  return (profiles || []).find((p) => p.installId === installId && p.profileId === profileId);
}

/** Start is a bounded, caller-owned session: only from a stopped binding, onto an online profile,
 *  and only against the revision the operator actually saw. */
export function canStart(binding, profiles) {
  if (!binding || binding.state !== 'STOPPED' || isStopping(binding)) return false;
  const profile = findProfile(profiles, binding.installId, binding.profileId);
  return !!profile && profile.online === true;
}

/** Stop is allowed while a session is pending or already running - never while it is already
 *  stopped, which would just be a duplicate command. */
export function canStop(binding) {
  return !!binding && ['RUNNING', 'PENDING', 'QUEUED', 'WAITING', 'NEEDS_REVIEW'].includes(binding.state) && binding.desired !== 'STOP';
}

export function commandPayload(operation, workflow, binding) {
  return { operation, workflow, revision: binding.revision };
}

function errorMessage(err, fallback) {
  return err?.response?.data?.message || err?.response?.data?.detail || fallback;
}

const WORKFLOWS = [
  { value: 'smm', label: 'Start SMM delivery' },
  { value: 'feed', label: 'Check feed (5-item pilot)' },
  { value: 'explore', label: 'Check Explore (5-item pilot)' },
  { value: 'follow', label: 'Run Follow' },
  { value: 'engagement', label: 'Run Likes / Comments' },
  { value: 'outreach', label: 'Run outbound DMs' },
  { value: 'story', label: 'Run Stories' },
];

/** Workflows that run the slot's saved native settings against its target history, so they all need the
 *  account's existing engagement history imported first. Story joins them on exactly the same terms - its
 *  extra outbound-DM condition only applies when that native setting is on, which only the server knows. */
export const MODULE_WORKFLOWS = ['follow', 'engagement', 'outreach', 'story'];

/** Read-only: runs on the companion's own saved native settings, no history ledger applies. Kept
 *  out of WORKFLOWS and given its own button so it isn't mistaken for the feed/explore pilot checks. */
const BROWSE_WORKFLOW = { value: 'browse', label: 'Run saved browsing' };

/*
 * SMM order history, as the server reports it. The dashboard never decides any of this: `ready` is the
 * server's own derivation, and `declaration` is a first-use record the owner already made here, which
 * the companion still has to check against the PC's real files before anything counts.
 */

/** One honest sentence about where this slot's native order history stands. */
export function smmHistorySummary(binding) {
  const history = binding?.smmHistory || {};
  const ready = history.ready === true || (history.ready === undefined && binding?.historyReady === true);
  if (ready && history.state === 'DECLARED_ABSENT') {
    return { ready: true, tone: 'ok', text: 'Recorded: this account has never delivered native SMM orders.' };
  }
  if (ready) return { ready: true, tone: 'ok', text: 'This account’s existing order history has been imported and is protected.' };
  if (history.state === 'DECLARATION_PENDING') {
    return {
      ready: false,
      tone: 'pending',
      text: 'Your declaration is recorded. The browser companion on that PC is checking every slot you named for existing order history before it counts.',
    };
  }
  if (history.reviewRequired === true) {
    return {
      ready: false,
      tone: 'review',
      text: history.message || 'Order history was imported but is still held for review, so SMM delivery cannot start yet.',
    };
  }
  return {
    ready: false,
    tone: 'missing',
    text: 'This account’s existing SMM order history has not been set up yet. Missing files are never treated as “no orders”.',
  };
}

/** True when the owner still has something to do before SMM can run on this slot. */
export function needsSmmHistorySetup(binding) {
  return !!binding && !smmHistorySummary(binding).ready;
}

/** The single reason a start button is unavailable, or null when the dashboard has nothing against it.
 *  Only what the overview already told us - admission, quotas and protocol stay the server's answer. */
export function startBlockedReason(workflow, binding, profiles) {
  if (!canStart(binding, profiles)) return 'Requires a stopped connection to an online profile';
  if (workflow === 'smm' && binding.historyReady !== true) {
    return 'Set up this account’s existing order history first';
  }
  // Imported but incomplete evidence is held by the server, so the button stays off and says so.
  if (workflow === 'smm' && binding.smmHistory?.reviewRequired === true) {
    return 'Imported order history is still held for review';
  }
  if (workflow === 'outreach' && binding.outboundHistoryReady !== true) {
    return 'Import the account’s existing outbound message history through the companion first';
  }
  if (MODULE_WORKFLOWS.includes(workflow) && binding.moduleHistoryReady !== true) {
    return 'Import the account’s existing engagement history through the updated companion first';
  }
  return null;
}

const FIELD ='w-full rounded-lg border border-border bg-secondary/60 px-2 py-1.5 text-sm placeholder:text-muted-foreground/70';

function BindingForm({ slotName, expectedUsername, binding, profiles, onSave, saving }) {
  const [installId, setInstallId] = useState(binding?.installId || '');
  const [profileId, setProfileId] = useState(binding?.profileId || '');
  const [username, setUsername] = useState(binding?.expectedUsername || expectedUsername || '');

  const selected = findProfile(profiles, installId, profileId);

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
      <select
        className={FIELD}
        aria-label={`Browser profile for ${slotName}`}
        value={installId && profileId ? `${installId}::${profileId}` : ''}
        onChange={(e) => {
          const [inst, prof] = e.target.value.split('::');
          setInstallId(inst ? Number(inst) : '');
          setProfileId(prof || '');
        }}
        data-testid={`browser-profile-select-${slotName}`}
      >
        <option value="">Select PC + profile…</option>
        {(profiles || []).map((p) => (
          <option disabled={!p.online} key={`${p.installId}::${p.profileId}`} value={`${p.installId}::${p.profileId}`}>
            {p.installLabel} - {p.name}{p.online ? '' : ' (offline)'}
          </option>
        ))}
      </select>
      <input
        className={cn(FIELD, 'sm:col-span-2')}
        placeholder="Exact IG username"
        aria-label={`Instagram username for ${slotName}`}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        data-testid={`browser-username-input-${slotName}`}
      />
      <Button
        size="sm"
        disabled={saving || !selected?.online || !username.trim() || (binding && (binding.state !== 'STOPPED' || binding.desired !== 'STOP'))}
        onClick={() => onSave({ installId, profileId, expectedUsername: username.trim() })}
        data-testid={`browser-save-binding-${slotName}`}
      >
        Save connection
      </Button>
      {installId && profileId && selected && !selected.online && (
        <p className="col-span-full text-xs text-muted-foreground">
          That profile is offline. Connect its PC before changing this connection or starting it.
        </p>
      )}
    </div>
  );
}

/*
 * "Set up order history" - the one place the owner resolves a slot's native SMM history.
 *
 * Two genuinely different answers, never blurred together:
 *
 *   1. This account HAS delivered native SMM orders -> its existing history files must be read by the
 *      companion on that PC. This dashboard cannot browse that PC's filesystem, so it gives the exact
 *      local configuration to add there instead of pretending a path typed here is a local path.
 *   2. This account has NEVER delivered native SMM orders -> an explicit owner declaration for this
 *      exact account/slot, naming any previous slots it ran on. The companion then has to find every
 *      one of them absent before the server accepts it.
 *
 * There is no third option. Creating an empty history file, deleting history, or starting delivery
 * without it are not offered, because none of them is true or safe.
 */
function SmmHistorySetup({ slotName, binding, onDeclare, onWithdraw, busy }) {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState('');
  const [slotKeys, setSlotKeys] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const summary = smmHistorySummary(binding);
  const history = binding?.smmHistory || {};
  const pending = history.state === 'DECLARATION_PENDING';
  const username = binding?.expectedUsername || '';

  const configExample = `"smmHistorySources": {\n  "${slotName}": {\n    "paths": ["C:\\\\Users\\\\<you>\\\\NecroMancer IG\\\\smmOrderHistory\\\\${slotName}.txt"],\n    "acknowledgedComplete": true\n  }\n}`;

  return (
    <div className="space-y-2 rounded-lg border border-border/60 bg-background/40 p-3" data-testid={`smm-history-${slotName}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium">Order history</span>
        <Badge variant="outline" data-testid={`smm-history-state-${slotName}`}>
          {summary.ready ? 'Ready' : pending ? 'Checking on the PC' : summary.tone === 'review' ? 'Held for review' : 'Not set up'}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground" data-testid={`smm-history-summary-${slotName}`}>{summary.text}</p>
      {history.message && !pending && (
        <p className="text-[11px] text-muted-foreground" data-testid={`smm-history-message-${slotName}`}>{history.message}</p>
      )}

      {pending && (
        <div className="space-y-2">
          <p className="text-[11px] text-muted-foreground" data-testid={`smm-history-pending-${slotName}`}>
            Checking {(history.declaration?.slotKeys || []).join(', ') || slotName} on the connected PC. If any of them
            turns out to have order history, it is imported instead and this declaration is dropped - existing history is
            never overwritten or cleared.
          </p>
          <Button size="sm" variant="outline" disabled={busy} onClick={onWithdraw} data-testid={`smm-history-withdraw-${slotName}`}>
            Withdraw declaration
          </Button>
        </div>
      )}

      {!summary.ready && !pending && !open && (
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => setOpen(true)} data-testid={`smm-history-setup-${slotName}`}>
          Set up order history
        </Button>
      )}

      {!summary.ready && !pending && open && (
        <div className="space-y-3">
          <label className="flex items-start gap-2 text-xs">
            <input
              type="radio"
              name={`smm-history-choice-${slotName}`}
              checked={choice === 'import'}
              onChange={() => setChoice('import')}
              data-testid={`smm-history-choice-import-${slotName}`}
            />
            <span>
              <strong>Import this account’s existing order history.</strong> It has delivered native SMM orders before,
              on this or another PC.
            </span>
          </label>
          <label className="flex items-start gap-2 text-xs">
            <input
              type="radio"
              name={`smm-history-choice-${slotName}`}
              checked={choice === 'never'}
              onChange={() => setChoice('never')}
              data-testid={`smm-history-choice-never-${slotName}`}
            />
            <span>
              <strong>This account has never delivered native SMM orders.</strong> Record that as an owner declaration
              for {username || 'this account'}.
            </span>
          </label>

          {choice === 'import' && (
            <div className="space-y-2 rounded-md border border-border/60 p-2" data-testid={`smm-history-import-help-${slotName}`}>
              <p className="text-[11px] text-muted-foreground">
                The history files live on the PC running the browser companion, and this dashboard cannot read that PC’s
                filesystem. Add the file list to that PC’s companion configuration and it will import them on its next
                cycle. List every slot this account ever ran on - including slots on other PCs, copied over - and set
                <code className="mx-1">acknowledgedComplete</code> only when the list really is all of them.
              </p>
              <pre className="overflow-x-auto rounded bg-secondary/60 p-2 text-[10px]" data-testid={`smm-history-config-${slotName}`}>{configExample}</pre>
              <p className="text-[11px] text-muted-foreground">
                Native keeps them under <code>NecroMancer IG\smmOrderHistory\</code>, one file per slot. Leave those files
                exactly as they are: they are read, never edited, pruned or deleted.
              </p>
            </div>
          )}

          {choice === 'never' && (
            <div className="space-y-2 rounded-md border border-border/60 p-2">
              <p className="text-[11px] text-muted-foreground">
                This declares that <strong>{username}</strong> on {slotName} has never delivered a native SMM order. The
                companion still checks every slot named below on that PC first; if it finds any history, that history is
                imported instead.
              </p>
              <input
                className={FIELD}
                placeholder="Previous slots this account ran on, e.g. slot4, slot7 (optional)"
                aria-label={`Previous slots for ${slotName}`}
                value={slotKeys}
                onChange={(e) => setSlotKeys(e.target.value)}
                data-testid={`smm-history-slotkeys-${slotName}`}
              />
              <label className="flex items-start gap-2 text-[11px]">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  data-testid={`smm-history-confirm-${slotName}`}
                />
                <span>I confirm {username} has never delivered native SMM orders on any slot or PC.</span>
              </label>
              <Button
                size="sm"
                disabled={busy || !confirmed || !username}
                onClick={() => onDeclare(parseSlotKeys(slotKeys))}
                data-testid={`smm-history-declare-${slotName}`}
              >
                Record declaration for {username}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Free text -> the slot keys the server will bind the declaration to. The bound slot is added there. */
export function parseSlotKeys(text) {
  return (text || '')
    .split(/[\s,]+/)
    .map((key) => key.trim())
    .filter(Boolean);
}

/** The body of the owner declaration. It names one account, one slot and the revision they saw. */
export function declarationPayload(binding, slotKeys) {
  return {
    revision: binding.revision,
    expectedUsername: binding.expectedUsername,
    neverDeliveredNativeSmmOrders: true,
    slotKeys,
  };
}

function CommandRow({ slotName, binding, profiles, onCommand, busy }) {
  const startable = canStart(binding, profiles);
  const stoppable = canStop(binding);
  return (
    <div className="flex flex-wrap gap-2">
      {WORKFLOWS.map((w) => {
        const blocked = startBlockedReason(w.value, binding, profiles);
        return (
          <Button
            key={w.value}
            size="sm"
            variant="outline"
            disabled={!!blocked || busy}
            onClick={() => onCommand('start', w.value)}
            title={blocked || undefined}
            data-testid={`browser-start-${w.value}-${slotName}`}
          >
            {w.label}
          </Button>
        );
      })}
      <Button
        key={BROWSE_WORKFLOW.value}
        size="sm"
        variant="secondary"
        disabled={!startable || busy}
        onClick={() => onCommand('start', BROWSE_WORKFLOW.value)}
        title={startable ? undefined : 'Requires a stopped connection to an online profile'}
        data-testid={`browser-start-${BROWSE_WORKFLOW.value}-${slotName}`}
      >
        {BROWSE_WORKFLOW.label}
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={!stoppable || busy}
        onClick={() => onCommand('stop', binding?.workflow || 'smm')}
        data-testid={`browser-stop-${slotName}`}
      >
        Stop
      </Button>
    </div>
  );
}

/*
 * Per-slot Multilogin connection + controls. Reads GET /browser/overview (polled every 5s), writes
 * through PUT .../binding and POST .../command. `slots` is the same array SlotsPageNew already
 * fetches from slotsApi.list() - only slot_name/slot_id/ig_username are read here.
 */
export default function BrowserSlotsPanel({ slots }) {
  const queryClient = useQueryClient();
  const [busySlot, setBusySlot] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');

  const { data, error, isLoading } = useQuery({
    queryKey: ['browserOverview'],
    queryFn: () => api.get('/browser/overview').then((res) => res.data),
    refetchInterval: 5000,
    retry: false,
  });

  const unavailableStatus = error?.response?.status;
  const unavailable = !isLoading && (!!error || data?.enabled === false);

  const bindingsByName = useMemo(() => {
    const m = new Map();
    (data?.bindings || []).forEach((b) => m.set(b.slotName, b));
    return m;
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: ({ slotName, body }) => api.put(`/browser/slots/${encodeURIComponent(slotName)}/binding`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['browserOverview'] }),
    onError: (err) => toast.error(errorMessage(err, 'Could not save that connection')),
  });

  const declarationMutation = useMutation({
    mutationFn: ({ slotName, body }) => api.post(`/browser/slots/${encodeURIComponent(slotName)}/smm-history/declaration`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['browserOverview'] }),
    onError: (err) => toast.error(errorMessage(err, 'Could not record that declaration')),
    onSettled: () => setBusySlot(null),
  });

  const withdrawMutation = useMutation({
    mutationFn: ({ slotName, body }) => api.delete(`/browser/slots/${encodeURIComponent(slotName)}/smm-history/declaration`, { data: body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['browserOverview'] }),
    onError: (err) => toast.error(errorMessage(err, 'Could not withdraw that declaration')),
    onSettled: () => setBusySlot(null),
  });

  const commandMutation = useMutation({
    mutationFn: ({ slotName, body }) => api.post(`/browser/slots/${encodeURIComponent(slotName)}/command`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['browserOverview'] }),
    onError: (err) => toast.error(errorMessage(err, 'Command failed')),
    onSettled: () => setBusySlot(null),
  });

  if (isLoading) return null;

  if (unavailable) {
    return (
      <Card className="aurora-card" data-testid="browser-slots-unavailable">
        <CardContent className="p-4 sm:p-5 text-sm text-muted-foreground">
          Browser control is not available on this server{unavailableStatus ? ` (${unavailableStatus})` : ''}.
        </CardContent>
      </Card>
    );
  }

  const profiles = data.profiles || [];
  const chosenSlot = (slots || []).some(s => slotKey(s) === selectedSlot) ? selectedSlot : slotKey((slots || [])[0]);

  return (
    <Card className="aurora-card" data-testid="browser-slots-panel">
      <CardContent className="space-y-4 p-4 sm:p-5">
        <h2 className="font-semibold">Browser connections</h2>
        <div className="text-xs text-muted-foreground">
          Run the browser companion on the same PC as Multilogin, using that PC&apos;s existing paired desktop
          token - no secrets are sent to this dashboard.
        </div>
        <p className="text-xs text-muted-foreground">SMM runs bounded sessions automatically until stopped, following the server’s schedule and limits. Check feed / Check Explore are quick 5-item pilot checks. Run saved browsing uses this slot’s saved feed count, Explore probability/screens/posts and browsing duration. It pauses with an explanation if enabled settings require actions it cannot yet perform. Run Stories watches the story targets this slot is already set up for, using its saved story settings.</p>
        <select className={FIELD} aria-label="Browser slot" value={chosenSlot} onChange={e => setSelectedSlot(e.target.value)}>
          {(slots || []).map(slot => <option key={slotKey(slot)} value={slotKey(slot)}>{slotKey(slot)}{slot.ig_username ? ` · ${slot.ig_username}` : ''}</option>)}
        </select>
        {(slots || []).filter(slot => slotKey(slot) === chosenSlot).map((slot) => {
          const name = slotKey(slot);
          const binding = bindingsByName.get(name);
          return (
            <div key={name} className="space-y-2 rounded-xl border border-border/70 bg-secondary/30 p-3" data-testid={`browser-slot-row-${name}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-sm">{name}</span>
                <Badge variant="outline" data-testid={`browser-state-${name}`}>{stateLabel(binding)}</Badge>
              </div>
              {binding?.message && (
                <p className="text-xs text-muted-foreground" data-testid={`browser-message-${name}`}>{binding.message}</p>
              )}
              {binding && needsSmmHistorySetup(binding) && (
                <p className="text-xs text-warning">
                  Set up this account’s existing order history before starting SMM. A missing file is never treated as
                  “no orders”, and history is never cleared by changing profiles.
                </p>
              )}
              {binding && (
                <SmmHistorySetup
                  slotName={name}
                  binding={binding}
                  busy={busySlot === name && (declarationMutation.isPending || withdrawMutation.isPending)}
                  onDeclare={(slotKeys) => {
                    setBusySlot(name);
                    declarationMutation.mutate({ slotName: name, body: declarationPayload(binding, slotKeys) });
                  }}
                  onWithdraw={() => {
                    setBusySlot(name);
                    withdrawMutation.mutate({ slotName: name, body: { revision: binding.revision } });
                  }}
                />
              )}
              <BindingForm
                key={name + ':' + (binding?.revision ?? 'new')}
                slotName={name}
                expectedUsername={slot.ig_username}
                binding={binding}
                profiles={profiles}
                saving={saveMutation.isPending}
                onSave={(body) => saveMutation.mutate({ slotName: name, body })}
              />
              {binding && (
                <CommandRow
                  slotName={name}
                  binding={binding}
                  profiles={profiles}
                  busy={busySlot === name && commandMutation.isPending}
                  onCommand={(operation, workflow) => {
                    setBusySlot(name);
                    commandMutation.mutate({ slotName: name, body: commandPayload(operation, workflow, binding) });
                  }}
                />
              )}
              <p className="text-[11px] text-muted-foreground">
                Follow and Likes / Comments use this slot’s saved settings and direct-account targets.
                Import its existing engagement history through the updated companion before starting.
                Check feed and Check Explore are quick 5-item pilot checks only, not native settings-driven browsing.
                Run saved browsing has no history requirement since it is read-only; it applies this slot’s saved
                native app settings and reports the reason for any settings it can’t yet honor.
              </p>
              <p className="text-[11px] text-muted-foreground" data-testid={`browser-story-help-${name}`}>
                Run Stories uses this slot’s saved story settings, its usual targets and the same engagement
                history - nothing extra to fill in here. Each story target it finishes uses one of this slot’s
                comment targets for the session; it does not leave a comment on anyone’s post. Story likes,
                reactions and story replies are counted separately. If a story setting is switched on that the
                browser can’t do yet, the server refuses the start and says which setting stopped it.
              </p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
